import { useState, type ReactNode } from 'react'
import {
  IconCheck, IconCrown, IconDotsVertical, IconInfoCircle, IconSearch, IconTrash, IconUserPlus, IconUsersGroup,
} from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { projectApi, roleApi, teamApi, userApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import type { AccessEntry, ProjectRole, Role, Visibility } from '@/api/types'
import { useProject, useSystemCan } from '@/hooks/usePermissions'
import { LoadingState } from '@/components/common/States'
import { RoleBadge } from '@/components/common/RoleBadge'
import { useAuthStore } from '@/store/authStore'
import { formatDateShort } from '@/utils/format'
import { tr } from '@/i18n/tr'
import {
  Avatar, AvatarStack, Button, IconButton, Menu, MenuItem, Modal, Monogram, RadioCard, Select, TextField, cx, modals,
  notifications, useDebouncedValue,
} from '@/ui'
import './members.css'

/**
 * Rol seçimi için yardımcılar: hangi roller atanabilir, bir rolün izinleri benimkini aşıyor mu?
 *
 * <p>Sunucu aynı kuralları ayrıca uygular (ROLE_EXCEEDS_OWN_PERMISSIONS, OWNER_ROLE_IMMUTABLE,
 * TESTER_NOT_TEAM_ROLE); arayüz yalnız yapılamayacak seçeneği baştan göstermez.
 */
function useProjectRoles(project: MembersProject) {
  const roles = useQuery({ queryKey: ['roles', 'PROJECT'], queryFn: () => roleApi.list('PROJECT'), staleTime: 60_000 })
  const adminOverride = useSystemCan()('admin.projects.members')
  const mine = new Set(project.permissions ?? [])
  const byId = new Map((roles.data ?? []).map((r) => [r.id, r]))
  /** Bu rolü atayabilir, değiştirebilir ya da kaldırabilir miyim? */
  const canHandle = (role: Role | undefined) =>
    !!role && (adminOverride || role.permissions.every((p) => mine.has(p)))
  const assignable = (roles.data ?? []).filter((r) => r.code !== 'OWNER' && canHandle(r))
  return { list: roles.data ?? [], byId, canHandle, assignable, isLoading: roles.isLoading }
}

/** Varsayılan seçim: Düzenleyebilir atanabiliyorsa o, yoksa ilk atanabilir rol. */
function defaultRole(roles: Role[]): string | null {
  return (roles.find((r) => r.code === 'EDITOR') ?? roles[0])?.id ?? null
}

/** Paneli besleyen en az proje bilgisi: proje sekmesi ve Yönetim › Tüm projeler ortak. */
export interface MembersProject {
  id: string
  name: string
  visibility: Visibility
  memberCount: number
  /** Bu projedeki izinlerim (yönetimde boş: yetki sistem izninden gelir). */
  permissions: string[]
}

export function MembersTab() {
  const { project, can } = useProject()
  return (
    <MembersPanel project={project} canManage={can('members.manage')} canSettings={can('project.settings')}
      // Sahiplik devri izin değildir: yalnız projenin gerçek sahibi yapar.
      isOwner={project.myRole === 'OWNER'} />
  )
}

/** Değişiklikten sonra tazelenecek önbellekler (proje sekmesi + yönetim ekranı). */
function refreshAccess(qc: ReturnType<typeof useQueryClient>, projectId: string) {
  for (const key of [['members', projectId], ['access', projectId], ['project', projectId], ['project-teams', projectId],
    ['admin-project', projectId], ['admin-projects']]) {
    qc.invalidateQueries({ queryKey: key })
  }
}

/**
 * Doğrudan üyeler, ekipler ve yalnız ekip üzerinden erişenler.
 *
 * <p>{@code canManage}: kişi/ekip ekleme, rol değiştirme ({@code members.manage} ya da sistemin
 * {@code admin.projects.members} izni). Yönetim ekranında proje üyesi olmadan kullanılır.
 */
export function MembersPanel({ project, canManage, canSettings, isOwner }: {
  project: MembersProject
  canManage: boolean
  canSettings: boolean
  isOwner: boolean
}) {
  const me = useAuthStore((s) => s.user)
  const [addOpen, setAddOpen] = useState(false)
  const qc = useQueryClient()
  const roles = useProjectRoles(project)

  const query = useQuery({
    queryKey: ['members', project.id],
    queryFn: () => projectApi.members(project.id),
  })
  // Erişim kaynakları: üyenin ayrıca ekipten gelen erişimi ve yalnız ekipten erişenler.
  const access = useQuery({
    queryKey: ['access', project.id],
    queryFn: () => projectApi.access(project.id),
    enabled: project.visibility === 'SHARED',
  })
  const accessOf = new Map((access.data ?? []).map((a) => [a.userId, a]))

  /** "Yalnız ben" projeyi paylaşımlı yapıp ekleme penceresini açar. */
  const share = useMutation({
    mutationFn: () => projectApi.setVisibility(project.id, 'SHARED', false),
    onSuccess: () => {
      refreshAccess(qc, project.id)
      setAddOpen(true)
      notifications.show({ color: 'green', message: 'Proje paylaşımlı yapıldı; artık kişi ekleyebilirsiniz.' })
    },
    onError: (e) => notifications.show({ color: 'red', message: errorMessage(e) }),
  })

  const changeRole = useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string }) =>
      projectApi.changeMemberRole(project.id, userId, roleId),
    onSuccess: () => refreshAccess(qc, project.id),
    onError: (e) => notifications.show({ color: 'red', message: errorMessage(e) }),
  })

  /**
   * Sahipliği devreder.
   *
   * <p>Tek yönlü bir işlem: devirden sonra eski sahip Düzenleyebilir olur ve geri
   * alma yetkisi kalmaz. Onay metni bunu açıkça söylemek zorunda.
   */
  const transfer = useMutation({
    mutationFn: (userId: string) => projectApi.transferOwnership(project.id, userId),
    onSuccess: () => {
      refreshAccess(qc, project.id)
      qc.invalidateQueries({ queryKey: ['projects'] })
      notifications.show({ color: 'green', message: 'Proje sahipliği devredildi.' })
    },
    onError: (e) => notifications.show({ color: 'red', message: errorMessage(e) }),
  })

  const removeMember = useMutation({
    mutationFn: (userId: string) => projectApi.removeMember(project.id, userId),
    onSuccess: () => refreshAccess(qc, project.id),
    onError: (e) => notifications.show({ color: 'red', message: errorMessage(e) }),
  })

  if (project.visibility === 'PRIVATE') {
    return (
      <div className="nx-mem-private">
        <IconInfoCircle size={20} stroke={1.8} aria-hidden="true" />
        <div>
          <p>{tr.project.privateHint}</p>
          {canManage && canSettings ? (
            <>
              <p className="nx-mem-private__hint">
                Kişi ya da ekip eklemek için projenin “{tr.project.shared}” olması gerekir.
              </p>
              <Button size="sm" leftIcon={<IconUserPlus size={16} stroke={1.8} aria-hidden="true" />} loading={share.isPending}
                onClick={() => modals.openConfirmModal({
                  title: 'Proje paylaşımlı yapılsın mı?',
                  children: <>“{project.name}” <b>{tr.project.shared}</b> olur ve eklediğiniz kişiler projeye erişir.
                    İsterseniz daha sonra {tr.project.settings} sekmesinden geri alabilirsiniz.</>,
                  labels: { confirm: 'Paylaşımlı yap ve kişi ekle', cancel: tr.common.cancel },
                  onConfirm: () => share.mutate(),
                })}>
                Paylaşımlıya çevir ve kişi ekle
              </Button>
            </>
          ) : canSettings ? (
            <p className="nx-mem-private__hint">
              Başka kişileri ekleyebilmek için <b>{tr.project.settings}</b> sekmesinden görünürlüğü “{tr.project.shared}” yapın.
            </p>
          ) : canManage ? (
            <p className="nx-mem-private__hint">
              Bu proje yalnız sahibine açık; kişi eklenebilmesi için sahibinin projeyi “{tr.project.shared}” yapması gerekir.
            </p>
          ) : null}
        </div>
      </div>
    )
  }

  const uyeler = query.data ?? []
  // Rol özeti: projede kullanılan roller (Sahip her zaman), rol sırasıyla.
  const ozet = roles.list
    .map((r) => ({ role: r, n: uyeler.filter((m) => m.roleId === r.id).length }))
    .filter((x) => x.n > 0 || x.role.code === 'OWNER')

  return (
    <div className="nx-mem">
      <div className="nx-mem-roles">
        {ozet.map(({ role, n }) => (
          <div key={role.id} className="nx-mem-role">
            <span className="nx-mem-role__n nx-nums">{query.data ? n : '–'}</span>
            <span className="nx-mem-role__text">
              <RoleBadge role={legacyOf(role)} name={role.name} color={role.color} />
              <span>{role.description}</span>
            </span>
          </div>
        ))}
      </div>

      <section className="nx-mem-section" aria-labelledby="mem-h">
        <div className="nx-mem-section__head">
          <div>
            <h2 id="mem-h">Doğrudan üyeler</h2>
            {/* Sekme rozeti ekip üyeleri dahil erişen herkesi sayar; burada ikisi ayrı söylenir,
                yoksa "2 üye" ile rozetteki "3" çelişiyor gibi durur. */}
            <p className="nx-nums">
              {uyeler.length} doğrudan {tr.project.memberCount}
              {project.memberCount > uyeler.length && ` · ekiplerle birlikte ${project.memberCount} kişi erişebiliyor`}
            </p>
          </div>
          {canManage && roles.assignable.length > 0 && (
            <Button leftIcon={<IconUserPlus size={16} stroke={1.8} aria-hidden="true" />} onClick={() => setAddOpen(true)}>
              {tr.member.add}
            </Button>
          )}
        </div>

        {query.isLoading && <LoadingState />}

        {uyeler.length > 0 && (
          <div className="nx-panel nx-mem-table-wrap">
            <table className="nx-mem-table">
              <thead>
                <tr><th scope="col">Kullanıcı</th><th scope="col">{tr.member.role}</th><th scope="col">Eklenme</th><th scope="col"><span className="ui-visually-hidden">İşlemler</span></th></tr>
              </thead>
              <tbody>
                {uyeler.map((m) => {
                  const self = m.userId === me?.id
                  const role = roles.byId.get(m.roleId)
                  const owner = role?.code === 'OWNER' || m.role === 'OWNER'
                  // Kendinden güçlü üyenin rolü değiştirilemez, kendisi çıkarılamaz.
                  const handle = canManage && !owner && roles.canHandle(role)
                  return (
                    <tr key={m.userId}>
                      <td>
                        <span className="nx-mem-user">
                          <Avatar name={m.displayName} size={38} dark={owner} className="nx-mem-avatar" />
                          <span className="nx-mem-user__text">
                            <span className="nx-mem-user__name">{m.displayName}{self && <span> (siz)</span>}</span>
                            <span className="nx-mem-user__handle">@{m.username}</span>
                            {(accessOf.get(m.userId)?.teams.length ?? 0) > 0 && (
                              <span className="nx-mem-user__via">
                                Ayrıca: {accessOf.get(m.userId)!.teams.map((t) => `${t.teamName} (${t.roleName})`).join(', ')}
                              </span>
                            )}
                          </span>
                        </span>
                      </td>
                      <td>
                        {handle ? (
                          <Select size="sm" className="nx-mem-role-select" value={m.roleId}
                            aria-label={`${m.displayName} üyesinin rolü`}
                            data={roles.assignable.map((r) => ({ value: r.id, label: r.name }))}
                            onChange={(v) => v && v !== m.roleId && changeRole.mutate({ userId: m.userId, roleId: v })} />
                        ) : (
                          <RoleBadge role={m.role} name={m.roleName} color={role?.color} />
                        )}
                      </td>
                      <td className="nx-mem-muted nx-nums">{formatDateShort(m.addedAt)}</td>
                      <td className="nx-mem-act">
                        {!owner && (handle || self || (isOwner && m.role !== 'TESTER')) && (
                          <Menu align="end" width={230} trigger={
                            <IconButton label="Üye işlemleri"><IconDotsVertical size={17} stroke={1.8} /></IconButton>
                          }>
                            {/* İçerik göremeyen üyeye sahiplik verilmez (sunucu TESTER_CANNOT_BE_OWNER döner). */}
                            {isOwner && !self && m.role !== 'TESTER' && (
                              <MenuItem icon={<IconCrown size={16} />}
                                onSelect={() => modals.openConfirmModal({
                                  title: tr.project.transferOwnership,
                                  children: <><b>{m.displayName}</b> projenin yeni sahibi olacak. Siz <b>Düzenleyebilir</b> rolüne
                                    düşeceksiniz ve bu işlemi geri alamayacaksınız — sahipliği yalnızca yeni sahip devredebilir.</>,
                                  labels: { confirm: tr.project.transferOwnership, cancel: tr.common.cancel },
                                  confirmProps: { color: 'orange' },
                                  onConfirm: () => transfer.mutate(m.userId),
                                })}>
                                {tr.project.transferOwnership}
                              </MenuItem>
                            )}
                            {(handle || self) && (
                              <MenuItem danger icon={<IconTrash size={16} />}
                                onSelect={() => modals.openConfirmModal({
                                  title: self ? tr.member.leave : 'Üye çıkarılsın mı?',
                                  children: <RemoveNote self={self} name={m.displayName} teams={accessOf.get(m.userId)?.teams ?? []} />,
                                  labels: { confirm: tr.common.confirm, cancel: tr.common.cancel },
                                  confirmProps: { color: 'red' },
                                  onConfirm: () => removeMember.mutate(m.userId),
                                })}>
                                {self ? tr.member.leave : 'Üyeyi çıkar'}
                              </MenuItem>
                            )}
                          </Menu>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <AddMemberModal projectId={project.id} opened={addOpen} onClose={() => setAddOpen(false)}
        existing={uyeler.map((m) => m.userId)} roles={roles.assignable} />

      <TeamsSection project={project} canManage={canManage} roles={roles} />

      <TeamOnlyAccess entries={(access.data ?? []).filter((a) => !a.direct)} />
    </div>
  )
}

/**
 * Çıkarma onayı. Kişi bir ekip üzerinden de erişiyorsa bunu söyler: doğrudan üyeliği
 * kaldırmak erişimini kesmez (izinler birleşir), yalnız ekip rolüyle kalır.
 */
function RemoveNote({ self, name, teams }: { self: boolean; name: string; teams: AccessEntry['teams'] }) {
  return (
    <>
      {self ? tr.project.leaveConfirm : tr.member.removeConfirm}
      {teams.length > 0 && (
        <p className="nx-mem-warn" role="note">
          <IconInfoCircle size={16} stroke={1.8} aria-hidden="true" />
          <span>
            <b>{self ? 'Siz' : name}</b> {teams.map((t) => `${t.teamName} ekibi üzerinden ${t.roleName}`).join(', ')} rolüyle
            erişmeye devam {self ? 'edeceksiniz' : 'edecek'}. Erişimin tamamen kalkması için ekipten de çıkarılmalı.
          </span>
        </p>
      )}
    </>
  )
}

/** Doğrudan üye olmayıp yalnız ekip üzerinden erişenler. */
function TeamOnlyAccess({ entries }: { entries: AccessEntry[] }) {
  if (!entries.length) return null
  return (
    <section className="nx-mem-section" aria-labelledby="mem-v">
      <div className="nx-mem-section__head">
        <div>
          <h2 id="mem-v">Ekip üzerinden erişenler <span className="nx-mem-count nx-nums">{entries.length}</span></h2>
          <p className="nx-mem-section__hint">Doğrudan üye değiller; ekipleri projeye atandığı için erişiyorlar.</p>
        </div>
      </div>
      <ul className="nx-panel nx-mem-via">
        {entries.map((a) => (
          <li key={a.userId} className="nx-mem-via__row">
            <Avatar name={a.displayName} size={32} />
            <span className="nx-mem-user__text">
              <span className="nx-mem-user__name">{a.displayName}</span>
              <span className="nx-mem-user__handle">@{a.username}</span>
            </span>
            <span className="nx-mem-via__teams">
              {a.teams.map((t) => (
                <span key={t.teamId} className="nx-mem-via__chip">
                  <IconUsersGroup size={13} stroke={1.8} aria-hidden="true" />{t.teamName} · {t.roleName}
                </span>
              ))}
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}

/** Rolün eski adlara izdüşümü (yalnız rozet tonu için; sunucunun kuralıyla aynı). */
function legacyOf(role: Role): ProjectRole {
  if (role.code === 'OWNER') return 'OWNER'
  if (role.permissions.includes('notes.edit')) return 'EDITOR'
  if (role.permissions.includes('notes.view')) return 'VIEWER'
  return 'TESTER'
}

/**
 * Projeye atanmış ekipler.
 *
 * <p>Ekip üyeleri tek tek üye listesine kopyalanmaz: ekibe katılan kişi
 * projeye hemen erişir, ayrılan hemen kaybeder. Bir kişi hem doğrudan hem
 * ekip üzerinden erişiyorsa izinleri birleşir.
 */
function TeamsSection({ project, canManage, roles }: {
  project: MembersProject
  canManage: boolean
  roles: ReturnType<typeof useProjectRoles>
}) {
  const projectId = project.id
  const [assignOpen, setAssignOpen] = useState(false)
  const qc = useQueryClient()
  // Test eden şablonu ekibe verilmez (sunucu TESTER_NOT_TEAM_ROLE döner).
  const teamRoles = roles.assignable.filter((r) => r.code !== 'TESTER')

  const teams = useQuery({
    queryKey: ['project-teams', projectId],
    queryFn: () => projectApi.teams(projectId),
  })

  const yenile = () => refreshAccess(qc, projectId)

  const changeRole = useMutation({
    mutationFn: ({ teamId, roleId }: { teamId: string; roleId: string }) =>
      projectApi.changeTeamRole(projectId, teamId, roleId),
    onSuccess: yenile,
    onError: (e) => notifications.show({ color: 'red', message: errorMessage(e) }),
  })

  const remove = useMutation({
    mutationFn: (teamId: string) => projectApi.removeTeam(projectId, teamId),
    onSuccess: () => {
      yenile()
      notifications.show({ color: 'green', message: 'Ekip projeden kaldırıldı.' })
    },
    onError: (e) => notifications.show({ color: 'red', message: errorMessage(e) }),
  })

  const list = teams.data ?? []

  return (
    <section className="nx-mem-section" aria-labelledby="mem-t">
      <div className="nx-mem-section__head">
        <div>
          <h2 id="mem-t">{tr.team.title} <span className="nx-mem-count nx-nums">{list.length}</span></h2>
          <p className="nx-mem-section__hint">{tr.team.projectHint}</p>
        </div>
        {canManage && teamRoles.length > 0 && (
          <Button variant="secondary" leftIcon={<IconUsersGroup size={16} stroke={1.8} aria-hidden="true" />} onClick={() => setAssignOpen(true)}>
            {tr.team.assign}
          </Button>
        )}
      </div>

      {teams.isLoading && <LoadingState />}

      {!teams.isLoading && list.length === 0 && (
        <p className="nx-panel nx-mem-empty">
          Bu projeye henüz ekip atanmadı.{canManage && ' Bir ekibi atadığınızda üyelerinin hepsi projeye erişir.'}
        </p>
      )}

      {list.length > 0 && (
        <div className="nx-mem-teams">
          {list.map((t) => {
            const role = roles.byId.get(t.roleId)
            const handle = canManage && roles.canHandle(role)
            return (
              <article key={t.teamId} className="nx-team-card" aria-label={t.name}>
                <div className="nx-team-card__head">
                  <Monogram name={t.name} size={42} radius={12} dark />
                  <span className="nx-team-card__titles">
                    <span className="nx-team-card__name">{t.name}</span>
                    <span className="nx-team-card__sub nx-nums">Eklenme: {formatDateShort(t.addedAt)}</span>
                  </span>
                  {handle && (
                    <IconButton label={`${t.name} ekibini kaldır`} tone="danger"
                      onClick={() => modals.openConfirmModal({
                        title: `"${t.name}" projeden kaldırılsın mı?`,
                        children: tr.team.removeFromProjectConfirm,
                        labels: { confirm: 'Kaldır', cancel: tr.common.cancel },
                        confirmProps: { color: 'red' },
                        onConfirm: () => remove.mutate(t.teamId),
                      })}>
                      <IconTrash size={17} stroke={1.8} />
                    </IconButton>
                  )}
                </div>
                <div className="nx-team-card__foot">
                  <span className="nx-team-card__people">
                    {t.members.length > 0
                      ? <AvatarStack people={t.members.map((m) => ({ id: m.id, name: m.displayName, title: `${m.displayName} (@${m.username})` }))} max={5} size={28} />
                      : null}
                    <span className="nx-nums">{t.members.length ? `${t.members.length} kişi` : 'Ekipte henüz kimse yok'}</span>
                  </span>
                  {handle ? (
                    <Select size="sm" className="nx-mem-role-select" value={t.roleId} aria-label={`${t.name} ekibinin rolü`}
                      data={teamRoles.map((r) => ({ value: r.id, label: r.name }))}
                      onChange={(v) => v && v !== t.roleId && changeRole.mutate({ teamId: t.teamId, roleId: v })} />
                  ) : (
                    <RoleBadge role={t.role} name={t.roleName} color={role?.color} />
                  )}
                </div>
              </article>
            )
          })}
        </div>
      )}

      <AssignTeamModal projectId={projectId} opened={assignOpen} onClose={() => setAssignOpen(false)}
        existing={list.map((t) => t.teamId)} roles={teamRoles} />
    </section>
  )
}

/** Arama kutusu + altında öneri listesi (tasarımdaki ekleme pencereleri). */
function PickList<T extends { id: string }>({
  label, placeholder, query, onQuery, items, selected, onSelect, render, empty, loading,
}: {
  label: string
  placeholder: string
  query: string
  onQuery: (q: string) => void
  items: T[]
  selected: string | null
  onSelect: (id: string) => void
  render: (item: T) => ReactNode
  empty: string
  loading?: boolean
}) {
  return (
    <div className="nx-pick">
      <TextField label={label} placeholder={placeholder} value={query} autoFocus
        leftSection={<IconSearch size={17} stroke={1.8} aria-hidden="true" />}
        onChange={(e) => onQuery(e.currentTarget.value)} />
      <div role="listbox" aria-label="Öneriler" className="nx-pick__list">
        {!loading && items.length === 0 && <span className="nx-pick__empty">{empty}</span>}
        {items.map((it) => (
          <button key={it.id} type="button" role="option" aria-selected={selected === it.id}
            className={cx('nx-pick__opt', selected === it.id && 'is-on')} onClick={() => onSelect(it.id)}>
            {render(it)}
            {selected === it.id && <IconCheck size={16} stroke={2.2} aria-hidden="true" className="nx-pick__check" />}
          </button>
        ))}
      </div>
    </div>
  )
}

/** Rol seçimi: atanabilir her rol bir kart (ad + açıklama). */
function RolePicker({ value, onChange, roles }: { value: string | null; onChange: (id: string) => void; roles: Role[] }) {
  return (
    <fieldset className="nx-mem-rolepick">
      <legend>{tr.member.role}</legend>
      <div role="radiogroup" aria-label={tr.member.role} className="nx-mem-rolepick__opts">
        {roles.map((r) => (
          <RadioCard key={r.id} checked={value === r.id} onSelect={() => onChange(r.id)} title={r.name}
            description={r.description ?? undefined} />
        ))}
      </div>
    </fieldset>
  )
}

function AssignTeamModal({
  projectId, opened, onClose, existing, roles,
}: {
  projectId: string
  opened: boolean
  onClose: () => void
  existing: string[]
  roles: Role[]
}) {
  const [search, setSearch] = useState('')
  const [aranan] = useDebouncedValue(search, 200)
  const [teamId, setTeamId] = useState<string | null>(null)
  const [picked, setRole] = useState<string | null>(null)
  const role = picked ?? defaultRole(roles)
  const qc = useQueryClient()

  const teams = useQuery({
    queryKey: ['team-search', aranan],
    queryFn: () => teamApi.search(aranan),
    enabled: opened,
  })

  const assign = useMutation({
    mutationFn: () => projectApi.assignTeam(projectId, teamId!, role!),
    onSuccess: (t) => {
      refreshAccess(qc, projectId)
      setTeamId(null)
      onClose()
      notifications.show({ color: 'green', message: `${t.name} ekibi eklendi; ${t.members.length} kişi projeye erişebilir.` })
    },
    onError: (e) => notifications.show({ color: 'red', message: errorMessage(e) }),
  })

  const items = (teams.data ?? []).filter((t) => !existing.includes(t.id))

  return (
    <Modal opened={opened} onClose={onClose} title={tr.team.assign} size={540}
      footer={<>
        <Button variant="secondary" onClick={onClose}>{tr.common.cancel}</Button>
        <Button disabled={!teamId || !role} loading={assign.isPending} onClick={() => assign.mutate()}>{tr.team.assign}</Button>
      </>}>
      <div className="nx-mem-modal">
        <PickList label="Ekip" placeholder="Ekip ara…" query={search} onQuery={setSearch} items={items} selected={teamId}
          onSelect={setTeamId} loading={teams.isLoading}
          empty={teams.data?.length ? 'Eşleşen ekiplerin hepsi zaten bu projede.' : 'Ekip bulunamadı. Ekipleri yöneticiler oluşturur.'}
          render={(t) => (
            <>
              <Monogram name={t.name} size={30} radius={8} dark />
              <span className="nx-pick__text"><span>{t.name}</span><span>{t.memberCount} kişi</span></span>
            </>
          )} />
        <RolePicker value={role} onChange={setRole} roles={roles} />
        <p className="nx-mem-modal__hint">Ekibin her üyesi bu rolle erişir.</p>
      </div>
    </Modal>
  )
}

function AddMemberModal({
  projectId, opened, onClose, existing, roles,
}: {
  projectId: string
  opened: boolean
  onClose: () => void
  existing: string[]
  roles: Role[]
}) {
  const [search, setSearch] = useState('')
  const [aranan] = useDebouncedValue(search, 200)
  const [userId, setUserId] = useState<string | null>(null)
  const [picked, setRole] = useState<string | null>(null)
  const role = picked ?? defaultRole(roles)
  const qc = useQueryClient()

  const users = useQuery({
    queryKey: ['user-search', aranan],
    queryFn: () => userApi.search(aranan),
    enabled: opened,
  })

  const add = useMutation({
    mutationFn: () => projectApi.addMember(projectId, userId!, role!),
    onSuccess: () => {
      refreshAccess(qc, projectId)
      setUserId(null)
      setSearch('')
      onClose()
    },
    onError: (e) => notifications.show({ color: 'red', message: errorMessage(e) }),
  })

  const me = useAuthStore((s) => s.user)
  const items = (users.data ?? []).filter((u) => !existing.includes(u.id) && u.id !== me?.id)

  return (
    <Modal opened={opened} onClose={onClose} title={tr.member.add} size={540}
      footer={<>
        <Button variant="secondary" onClick={onClose}>{tr.common.cancel}</Button>
        <Button disabled={!userId || !role} loading={add.isPending} onClick={() => add.mutate()}>{tr.member.add}</Button>
      </>}>
      <div className="nx-mem-modal">
        <PickList label="Kullanıcı" placeholder={tr.member.searchUser} query={search} onQuery={setSearch} items={items}
          selected={userId} onSelect={setUserId} loading={users.isLoading} empty="Kullanıcı bulunamadı"
          render={(u) => (
            <>
              <Avatar name={u.displayName} size={30} />
              <span className="nx-pick__text"><span>{u.displayName}</span><span>@{u.username}</span></span>
            </>
          )} />
        <RolePicker value={role} onChange={setRole} roles={roles} />
      </div>
    </Modal>
  )
}
